import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';
import { Client, deleteClient } from '../api/entities.js';
import { System } from '../api/entities.js';
import { safeFormatDate, DATE_FORMATS } from '../utils/dateHelpers.js';
import { Button } from "../components/ui/button.jsx";
import { Input } from "../components/ui/input.jsx";
import { Label } from "../components/ui/label.jsx";
import { Textarea } from "../components/ui/textarea.jsx";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "../components/ui/card.jsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select.jsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs.jsx";
import { Badge } from "../components/ui/badge.jsx";
import { Avatar, AvatarFallback } from "../components/ui/avatar.jsx";
import { Separator } from "../components/ui/separator.jsx";
import { Switch } from "../components/ui/switch.jsx";
import { Checkbox } from "../components/ui/checkbox.jsx";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog.jsx";
import { motion } from "framer-motion";
import { 
  ArrowLeft, 
  Plus, 
  X, 
  Building2, 
  Users, 
  MapPin, 
  Phone, 
  Mail,
  Globe,
  User as UserIcon,
  Save,
  AlertCircle,
  CheckCircle2,
  Building,
  FileText,
  Cpu,
  Calendar,
  Edit,
  Trash2,
  History,
  Monitor,
  Key,
  Lock,
  Eye,
  EyeOff,
  Server,
  Wifi,
  Network,
  Shield
} from 'lucide-react';

export default function EditClient() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('id');
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [availableSystems, setAvailableSystems] = useState([]);
  const [isLoadingSystems, setIsLoadingSystems] = useState(false);
  const [client, setClient] = useState({
    corporate_name: '',
    name: '',
    company_name: '',
    email: '',
    cnpj: '',
    phone: '',
    address: '',
    technical_contacts: [],
    status: 'active',
    systems: [],
    remote_access: {
      teamviewer: { id: '', password: '', notes: '', active: false },
      anydesk: { id: '', password: '', notes: '', active: false },
      terminal_services: { host: '', port: '', username: '', password: '', notes: '', active: false },
      chrome_remote: { email: '', notes: '', active: false },
      vpn: { host: '', username: '', password: '', notes: '', active: false },
      other_tools: []
    },
    created_date: null,
    updated_date: null
  });

  // Debug para monitorar mudanças no estado
  useEffect(() => {
    console.log('🔍 Estado do client mudou:', {
      cnpj: client.cnpj,
      status: client.status,
      corporate_name: client.corporate_name
    });
  }, [client.cnpj, client.status, client.corporate_name]);

  useEffect(() => {
    console.log('📍 useEffect chamado, clientId:', clientId);
    loadClient();
    loadSystems();
  }, [clientId]);

  const defaultRemoteAccess = {
    teamviewer: { id: '', password: '', notes: '', active: false },
    anydesk: { id: '', password: '', notes: '', active: false },
    terminal_services: { host: '', port: '', username: '', password: '', notes: '', active: false },
    chrome_remote: { email: '', notes: '', active: false },
    vpn: { host: '', username: '', password: '', notes: '', active: false },
    other_tools: []
  };

  const loadClient = async () => {
    if (!clientId) {
      navigate(createPageUrl('Clients'));
      return;
    }

    setIsLoading(true);
    try {
      const data = await Client.findById(clientId);
      console.log('📄 Dados do cliente carregados:', data);
      console.log('🖥️ Sistemas no data.systems:', data.systems);
      console.log('🖥️ Sistemas no metadata.systems:', data.metadata?.systems);
      
      // Compatibilidade retroativa: buscar em metadata se não existir direto
      const metadata = data.metadata || {};
      const clientSystems = data.systems || metadata.systems || [];
      console.log('🖥️ Sistemas finais que serão definidos no estado:', clientSystems);
      
      setClient({
        ...data,
        corporate_name: data.corporate_name || metadata.corporate_name || '',
        name: data.name || '',
        company_name: data.company_name || '',
        email: data.email || '',
        cnpj: data.cnpj || metadata.cnpj || '',
        phone: data.phone || '',
        address: data.address || '',
        status: data.status || 'active',
        technical_contacts: data.technical_contacts || metadata.technical_contacts || [],
        systems: clientSystems,
        remote_access: { ...defaultRemoteAccess, ... (data.remote_access || metadata.remote_access || {}) },
        created_date: data.created_date || null,
        updated_date: data.updated_date || null
      });
      console.log('✅ Estado do cliente após carregamento:', {
        cnpj: data.cnpj || metadata.cnpj || '',
        corporate_name: data.corporate_name || metadata.corporate_name || '',
        status: data.status || 'active'
      });
      
      console.log('📄 Verificação final dos campos importantes:', {
        'client.cnpj': client.cnpj,
        'client.corporate_name': client.corporate_name,
        'data.cnpj': data.cnpj,
        'data.corporate_name': data.corporate_name,
        'metadata.cnpj': metadata.cnpj,
        'metadata.corporate_name': metadata.corporate_name
      });
    } catch (error) {
      console.error('Error loading client:', error);
      alert('Erro ao carregar dados do cliente');
      navigate(createPageUrl('Clients'));
    }
    setIsLoading(false);
  };

  const loadSystems = async () => {
    setIsLoadingSystems(true);
    try {
      console.log('🔄 Carregando sistemas...');
      const systems = await System.findAll();
      console.log('📊 Sistemas carregados:', systems);
      setAvailableSystems(systems || []);
    } catch (error) {
      console.error('❌ Erro ao carregar sistemas:', error);
      setAvailableSystems([]);
    }
    setIsLoadingSystems(false);
  };

  const toggleSystem = (systemId) => {
    const isSelected = client.systems.some(s => s.system_id === systemId);
    
    if (isSelected) {
      // Remove system
      setClient({
        ...client,
        systems: client.systems.filter(s => s.system_id !== systemId)
      });
    } else {
      // Add system
      const system = availableSystems.find(s => s.id === systemId);
      if (system) {
        setClient({
          ...client,
          systems: [
            ...client.systems,
            {
              system_id: systemId,
              system_name: system.name,
              installation_date: '',
              license_type: 'standard',
              max_users: 1,
              notes: ''
            }
          ]
        });
      }
    }
  };

  const updateSystemConfig = (systemId, field, value) => {
    setClient({
      ...client,
      systems: client.systems.map(system => 
        system.system_id === systemId 
          ? { ...system, [field]: value }
          : system
      )
    });
  };

  const addContact = () => {
    setClient({
      ...client,
      technical_contacts: [
        ...(client.technical_contacts || []),
        { name: '', email: '', phone: '', role: '' }
      ]
    });
  };

  const removeContact = (index) => {
    const newContacts = [...client.technical_contacts];
    newContacts.splice(index, 1);
    setClient({ ...client, technical_contacts: newContacts });
  };

  const updateContact = (index, field, value) => {
    const newContacts = [...client.technical_contacts];
    newContacts[index] = { ...newContacts[index], [field]: value };
    setClient({ ...client, technical_contacts: newContacts });
  };

  const addSystem = () => {
    setClient({
      ...client,
      systems: [
        ...(client.systems || []),
        {
          system_id: '',
          version_id: '',
          url: '',
          host: '',
          database: '',
          username: '',
          password: '',
          tool: '',
          notes: ''
        }
      ]
    });
  };

  const removeSystem = (index) => {
    const newSystems = [...client.systems];
    newSystems.splice(index, 1);
    setClient({ ...client, systems: newSystems });
  };

  const updateSystem = (index, field, value) => {
    const newSystems = [...client.systems];
    newSystems[index] = { ...newSystems[index], [field]: value };
    setClient({ ...client, systems: newSystems });
  };

  // Gerenciar dados de acesso remoto
  const updateRemoteAccess = (tool, field, value) => {
    setClient({
      ...client,
      remote_access: {
        ...client.remote_access,
        [tool]: { ...client.remote_access[tool], [field]: value }
      }
    });
  };

  const addOtherTool = () => {
    setClient({
      ...client,
      remote_access: {
        ...client.remote_access,
        other_tools: [
          ...client.remote_access.other_tools,
          { name: '', credentials: '', notes: '', active: false }
        ]
      }
    });
  };

  const removeOtherTool = (index) => {
    const newTools = [...client.remote_access.other_tools];
    newTools.splice(index, 1);
    setClient({
      ...client,
      remote_access: { ...client.remote_access, other_tools: newTools }
    });
  };

  const updateOtherTool = (index, field, value) => {
    const newTools = [...client.remote_access.other_tools];
    newTools[index] = { ...newTools[index], [field]: value };
    setClient({
      ...client,
      remote_access: { ...client.remote_access, other_tools: newTools }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      console.log('💾 Dados do cliente sendo enviados:', client);
      console.log('🆔 ID do cliente:', clientId);
      console.log('🖥️ Sistemas do cliente:', client.systems);
      console.log('📋 Campos específicos do cliente:', {
        'client.cnpj': client.cnpj,
        'client.corporate_name': client.corporate_name,
        'client.name': client.name,
        'client.company_name': client.company_name
      });
      
      // Preparar dados para envio - remover campos que podem causar problemas
      const clientData = {
        corporate_name: client.corporate_name || client.company_name || client.name || '',
        name: client.name || client.corporate_name || client.company_name || '',
        company_name: client.company_name || client.corporate_name || client.name || '',
        email: client.email || '',
        cnpj: client.cnpj || '',
        phone: client.phone || '',
        address: client.address || '',
        status: client.status || 'active',
        remote_access: { ...defaultRemoteAccess, ...client.remote_access },
        technical_contacts: client.technical_contacts || [],
        systems: client.systems || [],
        // Não incluir campos de data que podem ser read-only
        // created_date: client.created_date,
        // updated_date: client.updated_date
      };
      
      // Remover campos vazios que podem causar problemas
      
      // Validar se pelo menos um campo de nome está preenchido
      if (!clientData.corporate_name && !clientData.name && !clientData.company_name) {
        alert('Por favor, preencha pelo menos o nome da empresa');
        setIsSaving(false);
        return;
      }
      
      console.log('📝 Dados limpos para envio:', clientData);
      console.log('🖥️ Sistemas nos dados limpos:', clientData.systems);
      
      const result = await Client.update(clientId, clientData);
      console.log('✅ Cliente atualizado com sucesso:', result);
      
      navigate(createPageUrl('Clients'));
    } catch (error) {
      console.error('❌ Error updating client:', error);
      console.error('❌ Error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint
      });
      
      let errorMessage = 'Erro ao atualizar cliente';
      if (error.message) {
        errorMessage += `: ${error.message}`;
      }
      
      alert(errorMessage);
    }
    
    setIsSaving(false);
  };

  const getClientInitials = () => {
    const name = client.corporate_name || client.company_name || client.name || 'Cliente';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleDelete = async () => {
    try {
      await deleteClient(clientId);
      navigate(createPageUrl('Clients'));
    } catch (error) {
      alert('Erro ao excluir cliente: ' + (error.message || error));
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center gap-6">
          <div className="h-10 w-10 bg-muted rounded-lg animate-pulse"></div>
          <div className="flex items-center gap-6 flex-1">
            <div className="h-16 w-16 bg-muted rounded-full animate-pulse"></div>
            <div>
              <div className="h-8 w-48 bg-muted rounded animate-pulse mb-2"></div>
              <div className="h-5 w-64 bg-muted rounded animate-pulse"></div>
            </div>
          </div>
        </div>
        
        <Card className="border-0 shadow-xl">
          <CardContent className="p-8">
            <div className="space-y-6">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <div className="h-4 w-24 bg-muted rounded animate-pulse"></div>
                    <div className="h-11 w-full bg-muted rounded animate-pulse"></div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-4 w-24 bg-muted rounded animate-pulse"></div>
                    <div className="h-11 w-full bg-muted rounded animate-pulse"></div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-6"
      >
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(createPageUrl('Clients'))}
          className="h-10 w-10"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        
        <div className="flex items-center gap-6 flex-1">
          <Avatar className="h-16 w-16 border-4 border-background shadow-lg">
            <AvatarFallback className="bg-gradient-to-br from-primary to-primary/80 text-white font-bold text-xl">
              {getClientInitials()}
            </AvatarFallback>
          </Avatar>
          
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
                {client.corporate_name || client.company_name || client.name || 'Cliente'}
              </h1>
              <Badge variant={client.status === 'active' ? 'default' : 'destructive'}>
                {client.status === 'active' ? (
                  <>
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Ativo
                  </>
                ) : (
                  <>
                    <X className="h-3 w-3 mr-1" />
                    Inativo
                  </>
                )}
              </Badge>
            </div>
            <p className="text-muted-foreground text-lg">
              Edite as informações do cliente
            </p>
            {client.created_date && (
              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                <Calendar className="h-3 w-3" />
                Cliente desde {safeFormatDate(client.created_date, DATE_FORMATS.PRETTY)}
              </p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate(createPageUrl('Clients'))}>
            Cancelar
          </Button>
          <Button variant="outline" onClick={() => navigate(createPageUrl(`ViewClient?id=${clientId}`))}>
            <History className="h-4 w-4 mr-2" />
            Visualizar
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" className="gap-2">
                <Trash2 className="h-4 w-4" />
                Excluir
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Excluir Cliente</AlertDialogTitle>
                <AlertDialogDescription>
                  Tem certeza que deseja excluir este cliente? Esta ação não pode ser desfeita.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={handleDelete}>
                  Excluir
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button 
            onClick={handleSubmit} 
            disabled={isSaving}
            className="gap-2 shadow-lg"
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Salvando...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Salvar Alterações
              </>
            )}
          </Button>
        </div>
      </motion.div>

      {/* Main Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="border-0 shadow-xl">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <CardHeader className="pb-0">
              <TabsList className="grid w-full grid-cols-5 bg-muted/50 p-1 h-12">
                <TabsTrigger value="basic" className="flex items-center gap-2 text-sm font-medium">
                  <Building2 className="h-4 w-4" />
                  Informações Básicas
                </TabsTrigger>
                <TabsTrigger value="contacts" className="flex items-center gap-2 text-sm font-medium">
                  <Users className="h-4 w-4" />
                  Contatos Técnicos
                </TabsTrigger>
                <TabsTrigger value="systems" className="flex items-center gap-2 text-sm font-medium">
                  <Cpu className="h-4 w-4" />
                  Sistemas ({client.systems?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="remote" className="flex items-center gap-2 text-sm font-medium">
                  <Monitor className="h-4 w-4" />
                  Acesso Remoto
                </TabsTrigger>
                <TabsTrigger value="additional" className="flex items-center gap-2 text-sm font-medium">
                  <FileText className="h-4 w-4" />
                  Informações Adicionais
                </TabsTrigger>
              </TabsList>
            </CardHeader>

            <CardContent className="pt-6">
              <form onSubmit={handleSubmit}>
                {/* Aba: Informações Básicas */}
                <TabsContent value="basic" className="space-y-6 mt-0">
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Building className="h-5 w-5 text-primary" />
                      Dados da Empresa
                    </h3>
                    
                    <div className="grid gap-6 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="corporate_name" className="text-sm font-medium">
                          Razão Social *
                        </Label>
                        <Input
                          id="corporate_name"
                          placeholder="Digite a razão social"
                          value={client.corporate_name}
                          onChange={(e) => setClient({ ...client, corporate_name: e.target.value })}
                          required
                          className="h-11"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="company_name" className="text-sm font-medium">
                          Nome Fantasia
                        </Label>
                        <Input
                          id="company_name"
                          placeholder="Digite o nome fantasia"
                          value={client.company_name}
                          onChange={(e) => setClient({ ...client, company_name: e.target.value })}
                          className="h-11"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="cnpj" className="text-sm font-medium">
                          CNPJ *
                        </Label>
                        <Input
                          id="cnpj"
                          placeholder="00.000.000/0000-00"
                          value={client.cnpj}
                          onChange={(e) => {
                            console.log('🔄 Mudando CNPJ de:', client.cnpj, 'para:', e.target.value);
                            setClient({ ...client, cnpj: e.target.value });
                          }}
                          required
                          className="h-11"
                        />
                        <div className="text-xs text-muted-foreground">
                          CNPJ atual: {client.cnpj || 'não preenchido'}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="status" className="text-sm font-medium">
                          Status
                        </Label>
                        <Select 
                          value={client.status} 
                          onValueChange={(value) => {
                            console.log('🔄 Mudando status de:', client.status, 'para:', value);
                            setClient({ ...client, status: value });
                          }}
                        >
                          <SelectTrigger className="h-11">
                            <SelectValue placeholder="Selecione o status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">
                              <div className="flex items-center gap-2">
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                                Ativo
                              </div>
                            </SelectItem>
                            <SelectItem value="inactive">
                              <div className="flex items-center gap-2">
                                <X className="h-4 w-4 text-red-600" />
                                Inativo
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <div className="text-xs text-muted-foreground">
                          Status atual: {client.status || 'não definido'}
                        </div>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Mail className="h-5 w-5 text-primary" />
                      Informações de Contato
                    </h3>
                    
                    <div className="grid gap-6 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-sm font-medium">
                          E-mail
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="exemplo@empresa.com"
                          value={client.email}
                          onChange={(e) => setClient({ ...client, email: e.target.value })}
                          className="h-11"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-sm font-medium">
                          Telefone
                        </Label>
                        <Input
                          id="phone"
                          placeholder="(11) 99999-9999"
                          value={client.phone}
                          onChange={(e) => setClient({ ...client, phone: e.target.value })}
                          className="h-11"
                        />
                      </div>

                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="address" className="text-sm font-medium">
                          Endereço
                        </Label>
                        <Textarea
                          id="address"
                          placeholder="Digite o endereço completo"
                          value={client.address}
                          onChange={(e) => setClient({ ...client, address: e.target.value })}
                          rows={3}
                          className="resize-none"
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Aba: Contatos Técnicos */}
                <TabsContent value="contacts" className="space-y-6 mt-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold flex items-center gap-2">
                        <Users className="h-5 w-5 text-primary" />
                        Contatos Técnicos
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Gerencie os contatos técnicos responsáveis pelo suporte
                      </p>
                    </div>
                    <Button 
                      type="button" 
                      onClick={addContact}
                      className="gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      Adicionar Contato
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {(client.technical_contacts || []).length === 0 ? (
                      <Card className="py-12 border-dashed border-2">
                        <CardContent className="text-center">
                          <UserIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                          <h4 className="text-lg font-medium mb-2">Nenhum contato adicionado</h4>
                          <p className="text-muted-foreground mb-4">
                            Adicione contatos técnicos para facilitar o suporte
                          </p>
                          <Button onClick={addContact} variant="outline">
                            <Plus className="h-4 w-4 mr-2" />
                            Adicionar Primeiro Contato
                          </Button>
                        </CardContent>
                      </Card>
                    ) : (
                      (client.technical_contacts || []).map((contact, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                        >
                          <Card>
                            <CardHeader className="flex flex-row items-center justify-between pb-3">
                              <CardTitle className="text-base flex items-center gap-2">
                                <UserIcon className="h-4 w-4" />
                                Contato {index + 1}
                              </CardTitle>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => removeContact(index)}
                                className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </CardHeader>
                            <CardContent className="grid gap-4 md:grid-cols-2">
                              <div className="space-y-2">
                                <Label className="text-sm font-medium">Nome</Label>
                                <Input
                                  placeholder="Nome do contato"
                                  value={contact.name}
                                  onChange={(e) => updateContact(index, 'name', e.target.value)}
                                  className="h-10"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-sm font-medium">Cargo</Label>
                                <Input
                                  placeholder="Cargo ou função"
                                  value={contact.role}
                                  onChange={(e) => updateContact(index, 'role', e.target.value)}
                                  className="h-10"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-sm font-medium">E-mail</Label>
                                <Input
                                  type="email"
                                  placeholder="email@empresa.com"
                                  value={contact.email}
                                  onChange={(e) => updateContact(index, 'email', e.target.value)}
                                  className="h-10"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-sm font-medium">Telefone</Label>
                                <Input
                                  placeholder="(11) 99999-9999"
                                  value={contact.phone}
                                  onChange={(e) => updateContact(index, 'phone', e.target.value)}
                                  className="h-10"
                                />
                              </div>
                            </CardContent>
                          </Card>
                        </motion.div>
                      ))
                    )}
                  </div>
                </TabsContent>

                {/* Aba: Sistemas */}
                <TabsContent value="systems" className="space-y-6 mt-0">
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Cpu className="h-5 w-5 text-primary" />
                      Sistemas Utilizados
                    </h3>
                    <p className="text-sm text-muted-foreground mb-6">
                      Marque os sistemas que o cliente utiliza
                    </p>

                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Sistemas Disponíveis</CardTitle>
                        <CardDescription>
                          Marque os sistemas que o cliente utiliza
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        {isLoadingSystems ? (
                          <div className="grid gap-3 md:grid-cols-2">
                            {[1, 2, 3, 4].map((i) => (
                              <div key={i} className="flex items-center space-x-3 p-3 border rounded-lg">
                                <div className="h-4 w-4 bg-muted rounded animate-pulse"></div>
                                <div className="flex-1">
                                  <div className="h-4 w-32 bg-muted rounded animate-pulse mb-1"></div>
                                  <div className="h-3 w-48 bg-muted rounded animate-pulse"></div>
                                </div>
                                <div className="h-5 w-16 bg-muted rounded animate-pulse"></div>
                              </div>
                            ))}
                          </div>
                        ) : availableSystems.length === 0 ? (
                          <div className="text-center py-8">
                            <Cpu className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                            <h4 className="text-lg font-medium mb-2">Nenhum sistema disponível</h4>
                            <p className="text-muted-foreground">
                              Não há sistemas cadastrados no momento
                            </p>
                          </div>
                        ) : (
                          <div className="grid gap-3 md:grid-cols-2">
                            {availableSystems.map((system) => {
                              const isSelected = client.systems.some(s => s.system_id === system.id);
                              console.log(`🔍 Sistema ${system.name} (ID: ${system.id}):`, {
                                isSelected,
                                clientSystems: client.systems,
                                systemsWithThisId: client.systems.filter(s => s.system_id === system.id)
                              });
                              return (
                                <div key={system.id} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-accent/50 transition-colors">
                                  <Checkbox
                                    id={`system-${system.id}`}
                                    checked={isSelected}
                                    onCheckedChange={() => toggleSystem(system.id)}
                                  />
                                  <div className="flex-1">
                                    <Label htmlFor={`system-${system.id}`} className="font-medium cursor-pointer">
                                      {system.name}
                                    </Label>
                                    {system.description && (
                                      <p className="text-sm text-muted-foreground">{system.description}</p>
                                    )}
                                  </div>
                                  <Badge variant="outline" className="text-xs">
                                    {system.type || 'Sistema'}
                                  </Badge>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>



                {/* Aba: Acesso Remoto */}
                <TabsContent value="remote" className="space-y-6 mt-0">
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Monitor className="h-5 w-5 text-primary" />
                      Dados de Acesso Remoto
                    </h3>
                    <p className="text-sm text-muted-foreground mb-6">
                      Configure os dados de acesso remoto para suporte técnico
                    </p>

                    <div className="space-y-6">
                      {/* TeamViewer */}
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Monitor className="h-4 w-4 text-blue-600" />
                            TeamViewer
                            <Switch
                              checked={client.remote_access.teamviewer.active}
                              onCheckedChange={(checked) => updateRemoteAccess('teamviewer', 'active', checked)}
                            />
                          </CardTitle>
                        </CardHeader>
                        {client.remote_access.teamviewer.active && (
                          <CardContent className="space-y-4">
                            <div className="grid gap-4 md:grid-cols-2">
                              <div className="space-y-2">
                                <Label className="text-sm">ID TeamViewer</Label>
                                <Input
                                  placeholder="123 456 789"
                                  value={client.remote_access.teamviewer.id}
                                  onChange={(e) => updateRemoteAccess('teamviewer', 'id', e.target.value)}
                                  className="h-9"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-sm">Senha</Label>
                                <Input
                                  type="text"
                                  placeholder="Senha de acesso"
                                  value={client.remote_access.teamviewer.password}
                                  onChange={(e) => updateRemoteAccess('teamviewer', 'password', e.target.value)}
                                  className="h-9"
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-sm">Observações</Label>
                              <Textarea
                                placeholder="Observações sobre o acesso TeamViewer..."
                                value={client.remote_access.teamviewer.notes}
                                onChange={(e) => updateRemoteAccess('teamviewer', 'notes', e.target.value)}
                                rows={2}
                                className="resize-none"
                              />
                            </div>
                          </CardContent>
                        )}
                      </Card>

                      {/* AnyDesk */}
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Server className="h-4 w-4 text-red-600" />
                            AnyDesk
                            <Switch
                              checked={client.remote_access.anydesk.active}
                              onCheckedChange={(checked) => updateRemoteAccess('anydesk', 'active', checked)}
                            />
                          </CardTitle>
                        </CardHeader>
                        {client.remote_access.anydesk.active && (
                          <CardContent className="space-y-4">
                            <div className="grid gap-4 md:grid-cols-2">
                              <div className="space-y-2">
                                <Label className="text-sm">ID AnyDesk</Label>
                                <Input
                                  placeholder="123 456 789"
                                  value={client.remote_access.anydesk.id}
                                  onChange={(e) => updateRemoteAccess('anydesk', 'id', e.target.value)}
                                  className="h-9"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-sm">Senha</Label>
                                <Input
                                  type="text"
                                  placeholder="Senha de acesso"
                                  value={client.remote_access.anydesk.password}
                                  onChange={(e) => updateRemoteAccess('anydesk', 'password', e.target.value)}
                                  className="h-9"
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-sm">Observações</Label>
                              <Textarea
                                placeholder="Observações sobre o acesso AnyDesk..."
                                value={client.remote_access.anydesk.notes}
                                onChange={(e) => updateRemoteAccess('anydesk', 'notes', e.target.value)}
                                rows={2}
                                className="resize-none"
                              />
                            </div>
                          </CardContent>
                        )}
                      </Card>

                      {/* Terminal Services / RDP */}
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Network className="h-4 w-4 text-green-600" />
                            Terminal Services / RDP
                            <Switch
                              checked={client.remote_access.terminal_services.active}
                              onCheckedChange={(checked) => updateRemoteAccess('terminal_services', 'active', checked)}
                            />
                          </CardTitle>
                        </CardHeader>
                        {client.remote_access.terminal_services.active && (
                          <CardContent className="space-y-4">
                            <div className="grid gap-4 md:grid-cols-3">
                              <div className="space-y-2">
                                <Label className="text-sm">Host/IP</Label>
                                <Input
                                  placeholder="192.168.1.100"
                                  value={client.remote_access.terminal_services.host}
                                  onChange={(e) => updateRemoteAccess('terminal_services', 'host', e.target.value)}
                                  className="h-9"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-sm">Porta</Label>
                                <Input
                                  placeholder="3389"
                                  value={client.remote_access.terminal_services.port}
                                  onChange={(e) => updateRemoteAccess('terminal_services', 'port', e.target.value)}
                                  className="h-9"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-sm">Usuário</Label>
                                <Input
                                  placeholder="usuário"
                                  value={client.remote_access.terminal_services.username}
                                  onChange={(e) => updateRemoteAccess('terminal_services', 'username', e.target.value)}
                                  className="h-9"
                                />
                              </div>
                            </div>
                            <div className="grid gap-4 md:grid-cols-2">
                              <div className="space-y-2">
                                <Label className="text-sm">Senha</Label>
                                <Input
                                  type="text"
                                  placeholder="Senha de acesso"
                                  value={client.remote_access.terminal_services.password}
                                  onChange={(e) => updateRemoteAccess('terminal_services', 'password', e.target.value)}
                                  className="h-9"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-sm">Observações</Label>
                                <Textarea
                                  placeholder="Observações sobre o acesso RDP..."
                                  value={client.remote_access.terminal_services.notes}
                                  onChange={(e) => updateRemoteAccess('terminal_services', 'notes', e.target.value)}
                                  rows={2}
                                  className="resize-none"
                                />
                              </div>
                            </div>
                          </CardContent>
                        )}
                      </Card>

                      {/* Chrome Remote Desktop */}
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Globe className="h-4 w-4 text-purple-600" />
                            Chrome Remote Desktop
                            <Switch
                              checked={client.remote_access.chrome_remote.active}
                              onCheckedChange={(checked) => updateRemoteAccess('chrome_remote', 'active', checked)}
                            />
                          </CardTitle>
                        </CardHeader>
                        {client.remote_access.chrome_remote.active && (
                          <CardContent className="space-y-4">
                            <div className="grid gap-4 md:grid-cols-2">
                              <div className="space-y-2">
                                <Label className="text-sm">E-mail da Conta Google</Label>
                                <Input
                                  type="email"
                                  placeholder="exemplo@gmail.com"
                                  value={client.remote_access.chrome_remote.email}
                                  onChange={(e) => updateRemoteAccess('chrome_remote', 'email', e.target.value)}
                                  className="h-9"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-sm">Observações</Label>
                                <Textarea
                                  placeholder="Observações sobre o acesso Chrome Remote..."
                                  value={client.remote_access.chrome_remote.notes}
                                  onChange={(e) => updateRemoteAccess('chrome_remote', 'notes', e.target.value)}
                                  rows={2}
                                  className="resize-none"
                                />
                              </div>
                            </div>
                          </CardContent>
                        )}
                      </Card>

                      {/* VPN */}
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Shield className="h-4 w-4 text-orange-600" />
                            VPN
                            <Switch
                              checked={client.remote_access.vpn.active}
                              onCheckedChange={(checked) => updateRemoteAccess('vpn', 'active', checked)}
                            />
                          </CardTitle>
                        </CardHeader>
                        {client.remote_access.vpn.active && (
                          <CardContent className="space-y-4">
                            <div className="grid gap-4 md:grid-cols-3">
                              <div className="space-y-2">
                                <Label className="text-sm">Servidor VPN</Label>
                                <Input
                                  placeholder="vpn.empresa.com"
                                  value={client.remote_access.vpn.host}
                                  onChange={(e) => updateRemoteAccess('vpn', 'host', e.target.value)}
                                  className="h-9"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-sm">Usuário</Label>
                                <Input
                                  placeholder="usuário VPN"
                                  value={client.remote_access.vpn.username}
                                  onChange={(e) => updateRemoteAccess('vpn', 'username', e.target.value)}
                                  className="h-9"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-sm">Senha</Label>
                                <Input
                                  type="text"
                                  placeholder="Senha VPN"
                                  value={client.remote_access.vpn.password}
                                  onChange={(e) => updateRemoteAccess('vpn', 'password', e.target.value)}
                                  className="h-9"
                                />
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-sm">Observações</Label>
                              <Textarea
                                placeholder="Observações sobre o acesso VPN..."
                                value={client.remote_access.vpn.notes}
                                onChange={(e) => updateRemoteAccess('vpn', 'notes', e.target.value)}
                                rows={2}
                                className="resize-none"
                              />
                            </div>
                          </CardContent>
                        )}
                      </Card>

                      {/* Outras Ferramentas */}
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Key className="h-4 w-4 text-gray-600" />
                            Outras Ferramentas
                          </CardTitle>
                          <CardDescription>
                            Adicione outras ferramentas de acesso remoto não listadas acima
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            {client.remote_access.other_tools.map((tool, index) => (
                              <Card key={index} className="border-l-4 border-l-gray-400">
                                <CardHeader className="pb-3">
                                  <div className="flex items-center justify-between">
                                    <CardTitle className="text-sm">Ferramenta {index + 1}</CardTitle>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => removeOtherTool(index)}
                                      className="h-8 w-8 text-red-500 hover:text-red-700"
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                  <div className="grid gap-4 md:grid-cols-3">
                                    <div className="space-y-2">
                                      <Label className="text-sm">Nome da Ferramenta</Label>
                                      <Input
                                        placeholder="Ex: LogMeIn, UltraVNC..."
                                        value={tool.name}
                                        onChange={(e) => updateOtherTool(index, 'name', e.target.value)}
                                        className="h-9"
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label className="text-sm">Credenciais</Label>
                                      <Input
                                        placeholder="ID, usuário, etc."
                                        value={tool.credentials}
                                        onChange={(e) => updateOtherTool(index, 'credentials', e.target.value)}
                                        className="h-9"
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label className="text-sm">Status</Label>
                                      <Switch
                                        checked={tool.active}
                                        onCheckedChange={(checked) => updateOtherTool(index, 'active', checked)}
                                      />
                                    </div>
                                  </div>
                                  <div className="space-y-2">
                                    <Label className="text-sm">Observações</Label>
                                    <Textarea
                                      placeholder="Observações sobre esta ferramenta..."
                                      value={tool.notes}
                                      onChange={(e) => updateOtherTool(index, 'notes', e.target.value)}
                                      rows={2}
                                      className="resize-none"
                                    />
                                  </div>
                                </CardContent>
                              </Card>
                            ))}
                            
                            <Button
                              type="button"
                              variant="outline"
                              onClick={addOtherTool}
                              className="w-full gap-2"
                            >
                              <Plus className="h-4 w-4" />
                              Adicionar Outra Ferramenta
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </TabsContent>

                {/* Aba: Sistemas */}
                <TabsContent value="systems" className="space-y-6 mt-0">
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Cpu className="h-5 w-5 text-primary" />
                      Sistemas Utilizados
                    </h3>
                    
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Selecione os sistemas que o cliente utiliza</CardTitle>
                        <CardDescription>
                          Marque os sistemas que serão utilizados pelo cliente para configurar o suporte adequado
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        {isLoadingSystems ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                            <span className="ml-3">Carregando sistemas...</span>
                          </div>
                        ) : availableSystems.length === 0 ? (
                          <div className="text-center py-8 text-muted-foreground">
                            <Cpu className="h-8 w-8 mx-auto mb-2 opacity-50" />
                            <p>Nenhum sistema cadastrado</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div className="grid gap-3 md:grid-cols-2">
                              {availableSystems.map((system) => {
                                const isSelected = client.systems.some(s => s.system_id === system.id);
                                return (
                                  <div key={system.id} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-accent/50 transition-colors">
                                    <Checkbox
                                      id={`system-${system.id}`}
                                      checked={isSelected}
                                      onCheckedChange={() => toggleSystem(system.id)}
                                    />
                                    <div className="flex-1">
                                      <Label htmlFor={`system-${system.id}`} className="font-medium cursor-pointer">
                                        {system.name}
                                      </Label>
                                      {system.description && (
                                        <p className="text-sm text-muted-foreground">{system.description}</p>
                                      )}
                                    </div>
                                    <Badge variant="outline" className="text-xs">
                                      {system.type || 'Sistema'}
                                    </Badge>
                                  </div>
                                );
                              })}
                            </div>

                            {/* Configurações dos sistemas selecionados */}
                            {client.systems.length > 0 && (
                              <div className="mt-6">
                                <h4 className="font-medium mb-3">Configurações dos Sistemas Selecionados</h4>
                                <div className="space-y-4">
                                  {client.systems.map((systemConfig) => (
                                    <Card key={systemConfig.system_id} className="border-l-4 border-l-primary">
                                      <CardHeader className="pb-3">
                                        <CardTitle className="text-base">{systemConfig.system_name}</CardTitle>
                                      </CardHeader>
                                      <CardContent className="grid gap-4 md:grid-cols-3">
                                        <div className="space-y-2">
                                          <Label className="text-sm">Data de Instalação</Label>
                                          <Input
                                            type="date"
                                            value={systemConfig.installation_date}
                                            onChange={(e) => updateSystemConfig(systemConfig.system_id, 'installation_date', e.target.value)}
                                            className="h-9"
                                          />
                                        </div>
                                        <div className="space-y-2">
                                          <Label className="text-sm">Tipo de Licença</Label>
                                          <Select 
                                            value={systemConfig.license_type} 
                                            onValueChange={(value) => updateSystemConfig(systemConfig.system_id, 'license_type', value)}
                                          >
                                            <SelectTrigger className="h-9">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="standard">Padrão</SelectItem>
                                              <SelectItem value="premium">Premium</SelectItem>
                                              <SelectItem value="enterprise">Enterprise</SelectItem>
                                              <SelectItem value="trial">Trial</SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </div>
                                        <div className="space-y-2">
                                          <Label className="text-sm">Máximo de Usuários</Label>
                                          <Input
                                            type="number"
                                            min="1"
                                            value={systemConfig.max_users}
                                            onChange={(e) => updateSystemConfig(systemConfig.system_id, 'max_users', parseInt(e.target.value) || 1)}
                                            className="h-9"
                                          />
                                        </div>
                                        <div className="space-y-2 md:col-span-3">
                                          <Label className="text-sm">Observações</Label>
                                          <Textarea
                                            placeholder="Observações específicas sobre este sistema..."
                                            value={systemConfig.notes}
                                            onChange={(e) => updateSystemConfig(systemConfig.system_id, 'notes', e.target.value)}
                                            rows={2}
                                            className="resize-none"
                                          />
                                        </div>
                                      </CardContent>
                                    </Card>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                {/* Aba: Informações Adicionais */}
                <TabsContent value="additional" className="space-y-6 mt-0">
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <FileText className="h-5 w-5 text-primary" />
                      Informações Adicionais
                    </h3>
                    
                    <div className="grid gap-6 md:grid-cols-2">
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            Datas Importantes
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {client.created_date && (
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Criado em:</span>
                              <span className="font-medium">
                                {safeFormatDate(client.created_date, DATE_FORMATS.FULL)}
                              </span>
                            </div>
                          )}
                          {client.updated_date && (
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Última atualização:</span>
                              <span className="font-medium">
                                {safeFormatDate(client.updated_date, DATE_FORMATS.FULL)}
                              </span>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base flex items-center gap-2">
                            <Building2 className="h-4 w-4" />
                            Resumo do Cliente
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Contatos técnicos:</span>
                            <Badge variant="outline">{(client.technical_contacts || []).length}</Badge>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Sistemas cadastrados:</span>
                            <Badge variant="outline">{(client.systems || []).length}</Badge>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Acessos remotos:</span>
                            <Badge variant="outline">
                              {Object.values(client.remote_access || {}).filter(tool => 
                                typeof tool === 'object' && tool.active
                              ).length + (client.remote_access?.other_tools?.filter(tool => tool.active).length || 0)}
                            </Badge>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Status:</span>
                            <Badge variant={client.status === 'active' ? 'default' : 'destructive'}>
                              {client.status === 'active' ? 'Ativo' : 'Inativo'}
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </TabsContent>
              </form>
            </CardContent>
          </Tabs>
        </Card>
      </motion.div>

      {/* Progress Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="fixed bottom-6 right-6"
      >
        <Card className="p-4 shadow-lg border-0">
          <div className="flex items-center gap-3 text-sm">
            <div className="flex items-center gap-2">
              {activeTab === 'basic' && <Building2 className="h-4 w-4 text-primary" />}
              {activeTab === 'contacts' && <Users className="h-4 w-4 text-primary" />}
              {activeTab === 'remote' && <Monitor className="h-4 w-4 text-primary" />}
              {activeTab === 'additional' && <FileText className="h-4 w-4 text-primary" />}
              <span className="font-medium">
                Etapa {activeTab === 'basic' ? '1' : activeTab === 'contacts' ? '2' : activeTab === 'remote' ? '3' : '4'} de 4
              </span>
            </div>
            <Badge variant="outline">
              {activeTab === 'basic' && 'Informações Básicas'}
              {activeTab === 'contacts' && 'Contatos'}
              {activeTab === 'remote' && 'Acesso Remoto'}
              {activeTab === 'additional' && 'Adicionais'}
            </Badge>
          </div>
        </Card>
      </motion.div>
    </div>
  );
}
